import ConversationItemType from 'models/conversation_item_type';

/**
 * User-facing groupings of conversation item types for the timeline filter.
 * A handful of channel/category buckets is far more usable than the 50+ raw
 * item types. Use `groupLabel(t, key)` to render a translated label — the
 * literal strings there are what i18n extraction picks up.
 *
 * Item types that don't belong to any group are intentionally never hidden by
 * the type filter — only types listed here can be toggled off.
 */
const TYPE_GROUPS = Object.freeze([
  {
    key: 'email',
    types: [
      ConversationItemType.EMAIL,
      ConversationItemType.EMAIL_CAMPAIGN_OUTREACH,
      ConversationItemType.EMAIL_SEND_FAILURE,
      ConversationItemType.MARKETING_EMAIL,
    ],
  },
  {
    key: 'sms',
    types: [ConversationItemType.SMS, ConversationItemType.SMS_CAMPAIGN_OUTREACH],
  },
  {
    key: 'phone',
    types: [
      ConversationItemType.PHONE_CALL,
      ConversationItemType.ABANDONED_CALL,
      ConversationItemType.PHONE_CALLBACK,
      ConversationItemType.CALL_FORWARD,
      ConversationItemType.VOICEMAIL,
      ConversationItemType.VOICE_RECORDING_DELETED,
    ],
  },
  {
    key: 'chat',
    types: [
      ConversationItemType.CHAT_MESSAGE,
      ConversationItemType.CHAT_ENDED,
      ConversationItemType.CHAT_CUSTOMER_URL,
      ConversationItemType.CHAT_SELF_SERVICE_METADATA,
      ConversationItemType.PROACTIVE_CHAT_CONTENT,
    ],
  },
  {
    key: 'messaging',
    types: [
      ConversationItemType.CONVERSATION_MESSAGE,
      ConversationItemType.FB_MESSAGE_INCOMING,
      ConversationItemType.FB_MESSAGE_OUTGOING,
      ConversationItemType.TWITTER,
      ConversationItemType.CUSTOM_CHANNEL_MESSAGE,
    ],
  },
  {
    key: 'notes',
    types: [ConversationItemType.CONVERSATION_NOTE],
  },
  {
    key: 'tasks',
    types: [
      ConversationItemType.TASK,
      ConversationItemType.TASK_COMPLETION,
      ConversationItemType.TASK_DUE,
      ConversationItemType.TASK_EDITED,
      ConversationItemType.TASK_REOPENED,
      ConversationItemType.TASK_ROUTING,
    ],
  },
  {
    key: 'ai',
    types: [
      ConversationItemType.AI_ACTIVITY,
      ConversationItemType.VOICE_AI_CALL,
      ConversationItemType.VOICE_AI_MESSAGE,
      ConversationItemType.VOICE_AI_SESSION_ENDED,
      ConversationItemType.VOICE_AI_RECORDING,
      ConversationItemType.MESSAGE_AUTOMATION_MESSAGE,
      ConversationItemType.MESSAGE_AUTOMATION_SUMMARY,
    ],
  },
  {
    key: 'statusChanges',
    types: [
      ConversationItemType.CONVERSATION_STATUS_CHANGE,
      ConversationItemType.TOPIC_CHANGE,
      ConversationItemType.ROUTING_ITEM,
      ConversationItemType.CUSTOMER_ASSIGNEE_CHANGE,
      ConversationItemType.CONVERSATION_CUSTOM_ATTRIBUTE_CHANGE,
      ConversationItemType.SESSION_ENDED,
      ConversationItemType.CUSTOMER_MERGE,
      ConversationItemType.NO_REPLY_NEEDED,
      ConversationItemType.RULE_DISABLED_LOOP,
      ConversationItemType.ITEM_LINK,
      ConversationItemType.SECURE_DATA_REQUEST,
      ConversationItemType.PAYMENT_STATUS_EVENT,
      ConversationItemType.PAYMENT_COMPLETED,
    ],
  },
  {
    key: 'activity',
    types: [
      ConversationItemType.CUSTOMER_ACTIVITY,
      ConversationItemType.WIDGET_ACTIVITY,
      ConversationItemType.MOBILE_CONTEXT,
      ConversationItemType.WEB_CONTEXT,
      ConversationItemType.HELP_APP_CONTEXT,
      ConversationItemType.CREDIT_CARD_VIEWED,
    ],
  },
]);

const GROUP_KEY_BY_TYPE = TYPE_GROUPS.reduce((memo, group) => {
  group.types.forEach(type => {
    memo[type] = group.key;
  });
  return memo;
}, {});

/**
 * Returns the type-group key a content type belongs to, or undefined if the
 * type is not part of any group (and therefore never hidden by the filter).
 */
export function typeGroupForContentType(contentType) {
  return GROUP_KEY_BY_TYPE[contentType];
}

/**
 * Translated display label for a type group. The strings are passed to `t()`
 * as literals (not a variable) so i18n extraction can find them — `t(variable)`
 * keys get pruned by the extractor.
 */
export function groupLabel(t, groupKey) {
  switch (groupKey) {
    case 'email':
      return t('Email');
    case 'sms':
      return t('SMS');
    case 'phone':
      return t('Phone & Voicemail');
    case 'chat':
      return t('Chat');
    case 'messaging':
      return t('Messaging & Social');
    case 'notes':
      return t('Notes');
    case 'tasks':
      return t('Tasks');
    case 'ai':
      return t('AI');
    case 'statusChanges':
      return t('Status & Audit');
    case 'activity':
      return t('Activity & Context');
    default:
      return groupKey;
  }
}

export default TYPE_GROUPS;
